import React, { useState, useEffect, useMemo } from "react";
import { X, Loader2, Save } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const ENVS = ["PRODUCTION", "DEVELOPMENT"]; // Go Live v1.0
const SECTIONS = ["General", "Localization", "Currency & Tax", "Invoice", "Email", "WhatsApp", "Payment", "Shipping", "Storage"];

const TRIGGER_CLS = "w-full bg-white/5 border-white/10 text-white hover:bg-white/10 focus:ring-emerald-500 data-[placeholder]:text-white/40";
const CONTENT_CLS = "z-[110] bg-[#0F0F17] border-white/10 text-white shadow-2xl";
const ITEM_CLS = "text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-emerald-500/20 data-[state=checked]:text-emerald-300";
const INPUT_CLS = "bg-white/5 border-white/10 text-white placeholder:text-white/30";

function Label({ children }) {
  return <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">{children}</label>;
}

function Field({ label, children }) {
  return <div>{label ? <Label>{label}</Label> : null}{children}</div>;
}

function TextField({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <Field label={label}>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={INPUT_CLS} data-testid={testid} />
    </Field>
  );
}

function ToggleField({ label, value, onChange, testid }) {
  return (
    <Field label={label}>
      <button type="button" onClick={() => onChange(!value)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-black uppercase tracking-wider transition ${
          value ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-white/60 border-white/10 hover:text-white"
        }`} data-testid={testid}>
        <span className={`w-2 h-2 rounded-full ${value ? "bg-emerald-400" : "bg-white/30"}`} />
        {value ? "Enabled" : "Disabled"}
      </button>
    </Field>
  );
}

function SecretField({ label, existingRef, onValueChange, valueKey, testid }) {
  const [visible, setVisible] = useState(false);
  const [val, setVal] = useState("");
  useEffect(() => { setVal(""); }, [existingRef]);
  return (
    <Field label={label}>
      <div className="flex items-center gap-2 flex-wrap">
        {existingRef ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-[10px] font-black uppercase tracking-wider font-mono">
            {existingRef.slice(0, 20)}…
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Belum ada secret</span>
        )}
        {visible ? (
          <Input
            type="password"
            autoComplete="new-password"
            placeholder="Masukkan secret baru (rotate)"
            value={val}
            onChange={(e) => { setVal(e.target.value); onValueChange(valueKey, e.target.value); }}
            className={INPUT_CLS}
            data-testid={testid}
          />
        ) : (
          <Button type="button" variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/10 text-xs" onClick={() => setVisible(true)}>
            {existingRef ? "Rotate secret" : "Set secret"}
          </Button>
        )}
      </div>
    </Field>
  );
}

const emptyForm = () => ({
  general: { app_name: "", business_name: "", support_email: "", support_phone: "", business_address: "" },
  localization: { timezone: "Asia/Jakarta", language: "id-ID", date_format: "DD/MM/YYYY", time_format: "24h" },
  currency: { currency_code: "IDR", currency_symbol: "Rp", decimal_digits: 0 },
  tax: { tax_enabled: false, tax_name: "PPN", tax_rate: 11.0, tax_included: false },
  invoice: { invoice_prefix: "INV", invoice_number_format: "INV-{YYYY}{MM}-{NNNN}", invoice_footer: "", show_tax: true, show_logo: true },
  email: { provider: "smtp", smtp_host: "", smtp_port: 587, sender_name: "", sender_email: "", enabled: false },
  whatsapp: { provider: "", phone_number: "", enabled: false },
  payment: { provider: "midtrans", mode: "sandbox", enabled: false },
  shipping: { provider: "jubelio", origin_address: "", origin_city: "", origin_postal_code: "", origin_coordinate: "", enabled: false },
  storage: { driver: "local", base_path: "/storage/tenant", bucket_name: "", region: "" },
});

export default function ConfigurationFormDialog({ tenants = [], existing = null, open, onClose, onSaved }) {
  const isEdit = !!existing;
  const [tenantId, setTenantId] = useState("");
  const [environment, setEnvironment] = useState("PRODUCTION");
  const [tab, setTab] = useState("General");
  const [form, setForm] = useState(null);
  const [secretValues, setSecretValues] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTab("General");
    setSecretValues({});
    if (existing) {
      setTenantId(existing.tenant_id);
      setEnvironment(existing.environment);
      const clone = {};
      Object.keys(emptyForm()).forEach((k) => { clone[k] = { ...(existing[k] || {}) }; });
      setForm(clone);
    } else {
      setTenantId(tenants[0]?.tenant_id || "");
      setEnvironment("PRODUCTION");
      setForm(emptyForm());
    }
  }, [open, existing, tenants]);

  const setSection = (name, patch) => setForm((prev) => ({ ...prev, [name]: { ...prev[name], ...patch } }));
  const setSecret = (fullKey, value) => setSecretValues((prev) => ({ ...prev, [fullKey]: value }));

  const buildPayload = useMemo(() => () => {
    if (!form) return {};
    const p = { ...form };
    // Inject `_value` secret fields on top of section objects
    Object.entries(secretValues).forEach(([k, v]) => {
      if (!v) return;
      // key format: "email.smtp_password" | "payment.server_key" | etc.
      const [sec, field] = k.split(".");
      p[sec] = { ...(p[sec] || {}), [`${field}_value`]: v };
    });
    return p;
  }, [form, secretValues]);

  const submit = async () => {
    if (!tenantId) { toast.error("Pilih tenant dulu."); return; }
    setBusy(true);
    try {
      const payload = buildPayload();
      let saved;
      if (isEdit) {
        const r = await api.put(`/platform/configurations/${existing.id}`, payload);
        saved = r.data.configuration;
        toast.success(`Config v${saved.config_version} diperbarui.`);
      } else {
        const r = await api.post("/platform/configurations", { tenant_id: tenantId, environment, ...payload });
        saved = r.data.configuration;
        toast.success(`Config v${saved.config_version} dibuat (DRAFT).`);
      }
      onSaved?.(saved);
      onClose?.();
    } catch (e) { notifyError(e, "Gagal simpan configuration"); }
    finally { setBusy(false); }
  };

  if (!open || !form) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()} data-testid="cfg-form">
      <div className="w-full max-w-3xl bg-[#0F0F17] border border-white/10 rounded-3xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="text-white font-display font-black text-lg">{isEdit ? `Edit Configuration · v${existing.config_version}` : "Buat Configuration"}</div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10">
            <X size={16} />
          </button>
        </div>

        {/* Header controls */}
        {!isEdit && (
          <div className="px-6 py-4 grid md:grid-cols-2 gap-3 border-b border-white/10">
            <Field label="Tenant *">
              <Select value={tenantId} onValueChange={setTenantId}>
                <SelectTrigger className={TRIGGER_CLS} data-testid="cfg-form-tenant"><SelectValue placeholder="Pilih tenant…" /></SelectTrigger>
                <SelectContent className={CONTENT_CLS}>
                  {tenants.map((t) => (<SelectItem key={t.tenant_id} value={t.tenant_id} className={ITEM_CLS}>{t.tenant_name} · {t.tenant_code}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Environment">
              <Select value={environment} onValueChange={setEnvironment}>
                <SelectTrigger className={TRIGGER_CLS} data-testid="cfg-form-env"><SelectValue /></SelectTrigger>
                <SelectContent className={CONTENT_CLS}>
                  {ENVS.map((e) => (<SelectItem key={e} value={e} className={ITEM_CLS}>{e}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        )}

        {/* Section Tabs */}
        <div className="px-4 pt-3 border-b border-white/10 flex gap-1 flex-wrap">
          {SECTIONS.map((s) => (
            <button key={s} onClick={() => setTab(s)} type="button"
              className={`px-3 py-2 text-[10px] font-black uppercase tracking-wider rounded-t-lg border-b-2 transition ${
                tab === s ? "text-emerald-300 border-emerald-400 bg-emerald-500/5" : "text-white/50 border-transparent hover:text-white hover:bg-white/5"
              }`} data-testid={`cfg-tab-${s.toLowerCase().replace(/[ &]/g, "-")}`}>
              {s}
            </button>
          ))}
        </div>

        <div className="p-6 overflow-auto flex-1 space-y-4">
          {tab === "General" && (
            <div className="grid gap-3 md:grid-cols-2">
              <TextField label="App Name" value={form.general.app_name} onChange={(v) => setSection("general", { app_name: v })} placeholder="Toko Anda" testid="cfg-general-app-name" />
              <TextField label="Business Name" value={form.general.business_name} onChange={(v) => setSection("general", { business_name: v })} placeholder="PT Toko Anda" testid="cfg-general-biz-name" />
              <TextField label="Support Email" value={form.general.support_email} onChange={(v) => setSection("general", { support_email: v })} type="email" placeholder="support@tokoanda.com" testid="cfg-general-email" />
              <TextField label="Support Phone" value={form.general.support_phone} onChange={(v) => setSection("general", { support_phone: v })} placeholder="+62 812…" testid="cfg-general-phone" />
              <div className="md:col-span-2">
                <TextField label="Business Address" value={form.general.business_address} onChange={(v) => setSection("general", { business_address: v })} placeholder="Jl. Contoh No.1, Jakarta" testid="cfg-general-address" />
              </div>
            </div>
          )}
          {tab === "Localization" && (
            <div className="grid gap-3 md:grid-cols-2">
              <TextField label="Timezone" value={form.localization.timezone} onChange={(v) => setSection("localization", { timezone: v })} placeholder="Asia/Jakarta" testid="cfg-loc-tz" />
              <TextField label="Language" value={form.localization.language} onChange={(v) => setSection("localization", { language: v })} placeholder="id-ID" testid="cfg-loc-lang" />
              <TextField label="Date Format" value={form.localization.date_format} onChange={(v) => setSection("localization", { date_format: v })} placeholder="DD/MM/YYYY" testid="cfg-loc-date" />
              <TextField label="Time Format" value={form.localization.time_format} onChange={(v) => setSection("localization", { time_format: v })} placeholder="24h" testid="cfg-loc-time" />
            </div>
          )}
          {tab === "Currency & Tax" && (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-3">
                <TextField label="Currency Code" value={form.currency.currency_code} onChange={(v) => setSection("currency", { currency_code: v })} placeholder="IDR" testid="cfg-cur-code" />
                <TextField label="Currency Symbol" value={form.currency.currency_symbol} onChange={(v) => setSection("currency", { currency_symbol: v })} placeholder="Rp" testid="cfg-cur-symbol" />
                <TextField label="Decimal Digits" value={String(form.currency.decimal_digits ?? 0)} onChange={(v) => setSection("currency", { decimal_digits: parseInt(v || "0", 10) })} type="number" testid="cfg-cur-decimals" />
              </div>
              <div className="grid gap-3 md:grid-cols-2 items-end">
                <ToggleField label="Tax Enabled" value={form.tax.tax_enabled} onChange={(v) => setSection("tax", { tax_enabled: v })} testid="cfg-tax-enabled" />
                <ToggleField label="Tax Included in Price" value={form.tax.tax_included} onChange={(v) => setSection("tax", { tax_included: v })} testid="cfg-tax-included" />
                <TextField label="Tax Name" value={form.tax.tax_name} onChange={(v) => setSection("tax", { tax_name: v })} placeholder="PPN" testid="cfg-tax-name" />
                <TextField label="Tax Rate (%)" value={String(form.tax.tax_rate ?? 0)} onChange={(v) => setSection("tax", { tax_rate: parseFloat(v || "0") })} type="number" testid="cfg-tax-rate" />
              </div>
            </div>
          )}
          {tab === "Invoice" && (
            <div className="grid gap-3 md:grid-cols-2">
              <TextField label="Prefix" value={form.invoice.invoice_prefix} onChange={(v) => setSection("invoice", { invoice_prefix: v })} placeholder="INV" testid="cfg-inv-prefix" />
              <TextField label="Number Format" value={form.invoice.invoice_number_format} onChange={(v) => setSection("invoice", { invoice_number_format: v })} placeholder="INV-{YYYY}{MM}-{NNNN}" testid="cfg-inv-format" />
              <div className="md:col-span-2">
                <TextField label="Invoice Footer" value={form.invoice.invoice_footer} onChange={(v) => setSection("invoice", { invoice_footer: v })} placeholder="Terima kasih telah berbelanja." testid="cfg-inv-footer" />
              </div>
              <ToggleField label="Show Tax on Invoice" value={form.invoice.show_tax} onChange={(v) => setSection("invoice", { show_tax: v })} testid="cfg-inv-show-tax" />
              <ToggleField label="Show Logo on Invoice" value={form.invoice.show_logo} onChange={(v) => setSection("invoice", { show_logo: v })} testid="cfg-inv-show-logo" />
            </div>
          )}
          {tab === "Email" && (
            <div className="grid gap-3 md:grid-cols-2">
              <TextField label="Provider" value={form.email.provider} onChange={(v) => setSection("email", { provider: v })} placeholder="smtp" testid="cfg-email-provider" />
              <ToggleField label="Enabled" value={form.email.enabled} onChange={(v) => setSection("email", { enabled: v })} testid="cfg-email-enabled" />
              <TextField label="SMTP Host" value={form.email.smtp_host} onChange={(v) => setSection("email", { smtp_host: v })} placeholder="smtp.gmail.com" testid="cfg-email-host" />
              <TextField label="SMTP Port" value={String(form.email.smtp_port ?? 587)} onChange={(v) => setSection("email", { smtp_port: parseInt(v || "587", 10) })} type="number" testid="cfg-email-port" />
              <TextField label="Sender Name" value={form.email.sender_name} onChange={(v) => setSection("email", { sender_name: v })} placeholder="Toko Anda" testid="cfg-email-sender-name" />
              <TextField label="Sender Email" value={form.email.sender_email} onChange={(v) => setSection("email", { sender_email: v })} placeholder="no-reply@tokoanda.com" testid="cfg-email-sender-email" />
              <SecretField label="SMTP Username" existingRef={form.email.smtp_username_ref} onValueChange={setSecret} valueKey="email.smtp_username" testid="cfg-email-username-secret" />
              <SecretField label="SMTP Password" existingRef={form.email.smtp_password_ref} onValueChange={setSecret} valueKey="email.smtp_password" testid="cfg-email-password-secret" />
            </div>
          )}
          {tab === "WhatsApp" && (
            <div className="grid gap-3 md:grid-cols-2">
              <TextField label="Provider" value={form.whatsapp.provider} onChange={(v) => setSection("whatsapp", { provider: v })} placeholder="fonnte / wapi" testid="cfg-wa-provider" />
              <ToggleField label="Enabled" value={form.whatsapp.enabled} onChange={(v) => setSection("whatsapp", { enabled: v })} testid="cfg-wa-enabled" />
              <TextField label="Phone Number" value={form.whatsapp.phone_number} onChange={(v) => setSection("whatsapp", { phone_number: v })} placeholder="+6281..." testid="cfg-wa-phone" />
              <SecretField label="API Key" existingRef={form.whatsapp.api_key_ref} onValueChange={setSecret} valueKey="whatsapp.api_key" testid="cfg-wa-key-secret" />
            </div>
          )}
          {tab === "Payment" && (
            <div className="grid gap-3 md:grid-cols-2">
              <TextField label="Provider" value={form.payment.provider} onChange={(v) => setSection("payment", { provider: v })} placeholder="midtrans" testid="cfg-pay-provider" />
              <TextField label="Mode" value={form.payment.mode} onChange={(v) => setSection("payment", { mode: v })} placeholder="sandbox / production" testid="cfg-pay-mode" />
              <ToggleField label="Enabled" value={form.payment.enabled} onChange={(v) => setSection("payment", { enabled: v })} testid="cfg-pay-enabled" />
              <div />
              <SecretField label="Merchant ID" existingRef={form.payment.merchant_id_ref} onValueChange={setSecret} valueKey="payment.merchant_id" testid="cfg-pay-mid-secret" />
              <SecretField label="Server Key" existingRef={form.payment.server_key_ref} onValueChange={setSecret} valueKey="payment.server_key" testid="cfg-pay-server-secret" />
              <SecretField label="Client Key" existingRef={form.payment.client_key_ref} onValueChange={setSecret} valueKey="payment.client_key" testid="cfg-pay-client-secret" />
            </div>
          )}
          {tab === "Shipping" && (
            <div className="grid gap-3 md:grid-cols-2">
              <TextField label="Provider" value={form.shipping.provider} onChange={(v) => setSection("shipping", { provider: v })} placeholder="jubelio / rajaongkir" testid="cfg-ship-provider" />
              <ToggleField label="Enabled" value={form.shipping.enabled} onChange={(v) => setSection("shipping", { enabled: v })} testid="cfg-ship-enabled" />
              <div className="md:col-span-2">
                <TextField label="Origin Address" value={form.shipping.origin_address} onChange={(v) => setSection("shipping", { origin_address: v })} placeholder="Jl. Gudang No. 1" testid="cfg-ship-address" />
              </div>
              <TextField label="Origin City" value={form.shipping.origin_city} onChange={(v) => setSection("shipping", { origin_city: v })} placeholder="Jakarta Selatan" testid="cfg-ship-city" />
              <TextField label="Origin Postal Code" value={form.shipping.origin_postal_code} onChange={(v) => setSection("shipping", { origin_postal_code: v })} placeholder="12345" testid="cfg-ship-postal" />
              <TextField label="Origin Coordinate (lat,lng)" value={form.shipping.origin_coordinate} onChange={(v) => setSection("shipping", { origin_coordinate: v })} placeholder="-6.2088, 106.8456" testid="cfg-ship-coord" />
            </div>
          )}
          {tab === "Storage" && (
            <div className="grid gap-3 md:grid-cols-2">
              <TextField label="Driver" value={form.storage.driver} onChange={(v) => setSection("storage", { driver: v })} placeholder="local / s3 / minio" testid="cfg-store-driver" />
              <TextField label="Base Path" value={form.storage.base_path} onChange={(v) => setSection("storage", { base_path: v })} placeholder="/storage/tenant" testid="cfg-store-path" />
              <TextField label="Bucket" value={form.storage.bucket_name} onChange={(v) => setSection("storage", { bucket_name: v })} placeholder="tenant-uploads" testid="cfg-store-bucket" />
              <TextField label="Region" value={form.storage.region} onChange={(v) => setSection("storage", { region: v })} placeholder="ap-southeast-1" testid="cfg-store-region" />
              <SecretField label="Access Key" existingRef={form.storage.access_key_ref} onValueChange={setSecret} valueKey="storage.access_key" testid="cfg-store-ak-secret" />
              <SecretField label="Secret Key" existingRef={form.storage.secret_key_ref} onValueChange={setSecret} valueKey="storage.secret_key" testid="cfg-store-sk-secret" />
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10">Batal</Button>
          <Button onClick={submit} disabled={busy || (!isEdit && !tenantId)} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="cfg-form-submit">
            {busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> Menyimpan…</> : <><Save size={14} className="mr-1" /> {isEdit ? "Update" : "Buat"}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
